import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import axiosConn from '../../axiosConn';
import JGLocalOpsMark from '../../components/common/JGLocalOpsMark';
import {
  canViewLocalOpsConcierge,
  LOCALOPS_CONCIERGE_ROUTE,
  LOCALOPS_OFFERING_CONCIERGE_ROUTE,
} from '../../utils/localOpsOpsAccess';
import {
  applyOfferingPresetToForm,
  buildDefaultOfferingConciergeForm,
  buildOfferingDraftPayload,
  isOfferingScriptSuggested,
  OFFERING_PRESETS,
  OFFERING_SCRIPT_CARDS,
  OFFERING_SOURCE_OPTIONS,
  OFFERING_USE_CASE_OPTIONS,
  trimOfferingValue,
} from '../../utils/localOpsOfferingConciergeIntelligence';
import './LocalOpsConcierge.css';

const normalizeRows = (payload) => {
  const data = payload?.data?.data || payload?.data || payload || [];
  return Array.isArray(data) ? data : [];
};

const FieldLabel = ({ children, optional = false }) => (
  <span className="localops-concierge__field-label">
    <span>{children}</span>
    <span className={optional ? 'localops-concierge__optional' : 'localops-concierge__required'}>
      {optional ? 'Optional' : 'Required'}
    </span>
  </span>
);

export default function LocalOpsOfferingConcierge() {
  const { player } = useContext(AuthContext);
  const allowed = canViewLocalOpsConcierge(player);
  const [form, setForm] = useState(buildDefaultOfferingConciergeForm);
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [copiedScript, setCopiedScript] = useState('');

  const authConfig = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${player?.token || ''}`,
      },
    }),
    [player?.token],
  );

  const fetchDrafts = useCallback(async () => {
    if (!allowed) return;
    setLoadingDrafts(true);
    try {
      const res = await axiosConn.get(
        'local-ops/concierge/offering-drafts?limit=25',
        authConfig,
      );
      setDrafts(normalizeRows(res));
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to load offering drafts.',
      );
    } finally {
      setLoadingDrafts(false);
    }
  }, [allowed, authConfig]);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  const updateField = (field, value) => {
    if (field === 'presetKey') {
      setForm((prev) => applyOfferingPresetToForm(prev, value));
      return;
    }
    if (field === 'offeringType' && value === 'service') {
      setForm((prev) => ({ ...prev, offeringType: value, quantityAvailable: '' }));
      return;
    }
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const copyText = async (title, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedScript(title);
      window.setTimeout(() => setCopiedScript(''), 1600);
    } catch (err) {
      setCopiedScript('');
    }
  };

  const validate = () => {
    if (!trimOfferingValue(form.sellerEmail)) return 'Seller email is required.';
    if (!trimOfferingValue(form.title)) return 'Offering title is required.';
    if (!trimOfferingValue(form.description)) return 'Offering description is required.';
    if (!trimOfferingValue(form.category)) return 'Category is required.';
    if (!Number.isFinite(Number(form.price)) || Number(form.price) < 0) {
      return 'Price is required.';
    }
    if (
      form.offeringType === 'product' &&
      (!Number.isInteger(Number(form.quantityAvailable)) ||
        Number(form.quantityAvailable) < 0)
    ) {
      return 'Product quantity is required.';
    }
    return '';
  };

  const submitDraft = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    const validation = validate();
    if (validation) {
      setError(validation);
      return;
    }
    setSubmitting(true);
    try {
      const createRes = await axiosConn.post(
        'local-ops/concierge/offering-drafts',
        buildOfferingDraftPayload(form),
        authConfig,
      );
      const draft = createRes?.data?.data?.draft || createRes?.data?.data;
      const draftId = draft?._id || draft?.id;
      if (!draftId) throw new Error('Offering draft was not created.');
      await axiosConn.post(
        `local-ops/concierge/offering-drafts/${draftId}/send-magic-link`,
        {},
        authConfig,
      );
      setNotice('Offering review link sent. Seller must review and publish.');
      setForm(buildDefaultOfferingConciergeForm());
      fetchDrafts();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to send offering draft.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!allowed) {
    return (
      <main className="localops-concierge">
        <section className="localops-concierge__hero">
          <JGLocalOpsMark size={34} />
          <div>
            <p className="localops-concierge__eyebrow">JG LocalOps</p>
            <h1>Offering Concierge</h1>
            <p>You do not have access to this LocalOps concierge workspace.</p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="localops-concierge">
      <section className="localops-concierge__hero">
        <div className="localops-concierge__brand">
          <JGLocalOpsMark size={36} />
          <div>
            <p className="localops-concierge__eyebrow">JG LocalOps</p>
            <h1>Offering Concierge</h1>
            <p>
              Prefill local offering drafts, send seller review links, and keep
              publish approval with the seller.
            </p>
          </div>
        </div>
        <div className="localops-concierge__hero-actions">
          <Link to={LOCALOPS_CONCIERGE_ROUTE}>Task LOC</Link>
          <Link to="/localops/ops">Ops Health</Link>
        </div>
      </section>

      <section className="localops-concierge__grid">
        <form className="localops-concierge__panel" onSubmit={submitDraft}>
          <div className="localops-concierge__panel-header">
            <h2>Offering Draft</h2>
            <span>SELLER_REVIEW_REQUIRED</span>
          </div>

          <div className="localops-concierge__pattern-grid">
            {OFFERING_PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                className={form.presetKey === preset.key ? 'is-selected' : ''}
                onClick={() => updateField('presetKey', preset.key)}
              >
                <strong>{preset.name}</strong>
                <small>{preset.summary}</small>
              </button>
            ))}
          </div>

          <label>
            <FieldLabel>Seller email</FieldLabel>
            <input
              value={form.sellerEmail}
              onChange={(event) => updateField('sellerEmail', event.target.value)}
              autoComplete="email"
            />
          </label>

          <div className="localops-concierge__field-row">
            <label>
              <FieldLabel optional>Seller name</FieldLabel>
              <input
                value={form.sellerName}
                onChange={(event) => updateField('sellerName', event.target.value)}
              />
            </label>
            <label>
              <FieldLabel optional>Phone</FieldLabel>
              <input
                value={form.sellerPhone}
                onChange={(event) => updateField('sellerPhone', event.target.value)}
              />
            </label>
          </div>

          <div className="localops-concierge__field-row">
            <label>
              <FieldLabel>Source</FieldLabel>
              <select
                value={form.source}
                onChange={(event) => updateField('source', event.target.value)}
              >
                {OFFERING_SOURCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <FieldLabel>Use case</FieldLabel>
              <select
                value={form.useCase}
                onChange={(event) => updateField('useCase', event.target.value)}
              >
                {OFFERING_USE_CASE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label>
            <FieldLabel optional>Source label</FieldLabel>
            <input
              value={form.sourceLabel}
              onChange={(event) => updateField('sourceLabel', event.target.value)}
              placeholder="Hip Camden DM, food truck call, Marketplace post"
            />
          </label>

          <div className="localops-concierge__field-row">
            <label>
              <FieldLabel>Offering type</FieldLabel>
              <select
                value={form.offeringType}
                onChange={(event) => updateField('offeringType', event.target.value)}
              >
                <option value="product">Product</option>
                <option value="service">Service</option>
              </select>
            </label>
            <label>
              <FieldLabel>Category</FieldLabel>
              <input
                value={form.category}
                onChange={(event) => updateField('category', event.target.value)}
              />
            </label>
          </div>

          <label>
            <FieldLabel>Title</FieldLabel>
            <input
              value={form.title}
              onChange={(event) => updateField('title', event.target.value)}
            />
          </label>

          <label>
            <FieldLabel>Description</FieldLabel>
            <textarea
              value={form.description}
              onChange={(event) => updateField('description', event.target.value)}
            />
          </label>

          <div className="localops-concierge__field-row">
            <label>
              <FieldLabel>Price</FieldLabel>
              <input
                value={form.price}
                onChange={(event) => updateField('price', event.target.value)}
                inputMode="decimal"
              />
            </label>
            <label>
              <FieldLabel optional={form.offeringType === 'service'}>Quantity</FieldLabel>
              <input
                value={form.quantityAvailable}
                onChange={(event) => updateField('quantityAvailable', event.target.value)}
                inputMode="numeric"
              />
            </label>
          </div>

          <label>
            <FieldLabel optional>Pickup / location note</FieldLabel>
            <input
              value={form.pickupAddress}
              onChange={(event) => updateField('pickupAddress', event.target.value)}
              placeholder="Seller can confirm exact location before publishing"
            />
          </label>

          <label>
            <FieldLabel optional>Seller-facing notes</FieldLabel>
            <textarea
              value={form.sellerFacingNotes}
              onChange={(event) => updateField('sellerFacingNotes', event.target.value)}
            />
          </label>

          <label>
            <FieldLabel optional>Original request / source post</FieldLabel>
            <textarea
              value={form.originalRequest}
              onChange={(event) => updateField('originalRequest', event.target.value)}
            />
          </label>

          <label>
            <FieldLabel optional>Internal notes</FieldLabel>
            <textarea
              value={form.internalNotes}
              onChange={(event) => updateField('internalNotes', event.target.value)}
            />
            <small>Internal notes are operator-only and are not sent to the seller.</small>
          </label>

          <div
            className="localops-concierge__preview localops-concierge__preview--single"
            data-testid="offering-concierge-seller-preview"
          >
            <h3>Seller preview</h3>
            <p>
              {form.title || 'Offering title'} - ${form.price || '0'}{' '}
              {form.category ? `in ${form.category}` : ''}
            </p>
            <p>{form.sellerFacingNotes}</p>
            <strong>Add a clear photo before publishing. Rich-share links perform better with strong visuals.</strong>
          </div>

          {error ? <p className="localops-concierge__error">{error}</p> : null}
          {notice ? <p className="localops-concierge__notice">{notice}</p> : null}

          <button type="submit" disabled={submitting}>
            {submitting ? 'Sending...' : 'Create draft & send review link'}
          </button>
        </form>

        <aside className="localops-concierge__panel" data-testid="offering-concierge-assist-panel">
          <div className="localops-concierge__panel-header">
            <h2>Concierge Assist</h2>
            <span>STATIC SCRIPTS</span>
          </div>
          <p className="localops-concierge__script-feature">
            Facebook creates attention. JG LocalOps turns that attention into
            organized local action with offer details, a request button, and the
            next step in one place.
          </p>
          <div className="localops-concierge__script-list">
            {OFFERING_SCRIPT_CARDS.map((card) => {
              const suggested = isOfferingScriptSuggested(card, form);
              return (
                <article
                  key={card.title}
                  className={`localops-concierge__script-card${
                    suggested ? ' is-relevant' : ''
                  }`}
                >
                  <div>
                    <strong>
                      {card.title}
                      {suggested ? (
                        <span className="localops-concierge__script-badge">
                          Suggested
                        </span>
                      ) : null}
                    </strong>
                    <span>{card.tab}</span>
                  </div>
                  <p>{card.text}</p>
                  <button type="button" onClick={() => copyText(card.title, card.text)}>
                    {copiedScript === card.title ? 'Copied' : 'Copy'}
                  </button>
                </article>
              );
            })}
          </div>
        </aside>
      </section>

      <section className="localops-concierge__panel">
        <div className="localops-concierge__panel-header">
          <h2>Recent Offering Drafts</h2>
          <span>{loadingDrafts ? 'Loading' : `${drafts.length} rows`}</span>
        </div>
        <div className="localops-concierge__draft-list">
          {drafts.map((draft) => (
            <article key={draft.id}>
              <strong>{draft.title}</strong>
              <span>{draft.status}</span>
              <small>{draft.sellerEmail}</small>
              <small>{draft.sourceLabel || draft.source}</small>
            </article>
          ))}
          {!drafts.length && !loadingDrafts ? (
            <p>No offering drafts yet.</p>
          ) : null}
        </div>
      </section>
    </main>
  );
}

export { OFFERING_SCRIPT_CARDS, LOCALOPS_OFFERING_CONCIERGE_ROUTE };
